/**
 * API Response Adapter
 * Transforms real SOE API responses to match the expected component structure
 */

import { DeviceUnlockResponse, ResponseInfo } from '@/store/sagas/types/deviceUnlock';
import { API_FIELD_IDS, getUnlockingPolicyFieldIds } from '@/lib/constants/apiFieldIds';
import { UI_TEXT_CONSTANTS } from '@/lib/constants/appConstants';
import { log } from '@/lib/utils/logger';

interface ApiSection {
  id?: string;
  value?: string;
  [key: string]: unknown;
}

interface ApiComponent {
  details?: {
    content?: {
      section?: ApiSection[];
    };
  };
  [key: string]: unknown;
}

interface RealApiResponse {
  responseInfo?: ResponseInfo;
  body?: {
    components?: ApiComponent[];
    main?: {
      components?: ApiComponent[];
    };
  };
  [key: string]: unknown;
}

/**
 * Transform real API response to match mock structure
 */
export const adaptHomePageResponse = (realApiResponse: RealApiResponse | DeviceUnlockResponse): DeviceUnlockResponse => {
  // If it's already in the correct format (mock), return as is
  if (realApiResponse?.body?.components) {
    return realApiResponse as DeviceUnlockResponse;
  }

  // Extract sections from real API response
  const sections = (realApiResponse as RealApiResponse)?.body?.main?.components?.[0]?.details?.content?.section ?? [];
  
  // Create a Map for efficient lookups
  const sectionsById = new Map<string, string>(sections.map((s: ApiSection) => [s.id ?? '', s.value ?? '']));
  
  // Group sections by their ID prefix to create structured components
  const titleText = sectionsById.get(API_FIELD_IDS.TITLE_TEXT) ?? UI_TEXT_CONSTANTS.DEFAULTS.CHECK_UNLOCK_STATUS;
  const titleDescription = sectionsById.get(API_FIELD_IDS.TITLE_DESCRIPTION) ?? '';
  const linkText = sectionsById.get(API_FIELD_IDS.LINK_TEXT) ?? UI_TEXT_CONSTANTS.DEFAULTS.UNLOCK_POLICY;
  const prefixLinkText = sectionsById.get(API_FIELD_IDS.PREFIX_LINK_TEXT) ?? UI_TEXT_CONSTANTS.DEFAULTS.VIEW_OUR;
  const titleUnlockingPolicy = sectionsById.get(API_FIELD_IDS.TITLE_UNLOCKING_POLICY) ?? UI_TEXT_CONSTANTS.DEFAULTS.DEVICE_UNLOCKING_POLICY;
  const checkStatusCTA = sectionsById.get(API_FIELD_IDS.CHECK_STATUS_CTA) ?? UI_TEXT_CONSTANTS.DEFAULTS.CHECK_STATUS;
  const inputText = sectionsById.get(API_FIELD_IDS.INPUT_TEXT) ?? UI_TEXT_CONSTANTS.DEFAULTS.IMEI_INPUT_LABEL;
  const imeiFieldRequired = sectionsById.get(API_FIELD_IDS.IMEI_FIELD_REQUIRED) ?? UI_TEXT_CONSTANTS.DEFAULTS.IMEI_FIELD_REQUIRED;
  const imeiFieldInvalid = sectionsById.get(API_FIELD_IDS.IMEI_FIELD_INVALID) ?? UI_TEXT_CONSTANTS.DEFAULTS.IMEI_FIELD_INVALID;
  const imeiToolTipCTA = sectionsById.get(API_FIELD_IDS.CLOSE_CTA) ?? UI_TEXT_CONSTANTS.DEFAULTS.CLOSE_CTA;
  const imeiToolTipTitle = sectionsById.get(API_FIELD_IDS.IMEI_TOOL_TIP_TITLE) ?? UI_TEXT_CONSTANTS.DEFAULTS.IMEI_TOOL_TIP_TITLE;
  const imeiToolTipContent = [
    sectionsById.get(API_FIELD_IDS.DESCRIPTION_IMEI_TOOLTIP1) ?? UI_TEXT_CONSTANTS.DEFAULTS.DESCRIPTION_IMEI_TOOLTIP1,
    sectionsById.get(API_FIELD_IDS.DESCRIPTION_IMEI_TOOLTIP2) ?? UI_TEXT_CONSTANTS.DEFAULTS.DESCRIPTION_IMEI_TOOLTIP2,
    sectionsById.get(API_FIELD_IDS.DESCRIPTION_IMEI_TOOLTIP3) ?? UI_TEXT_CONSTANTS.DEFAULTS.DESCRIPTION_IMEI_TOOLTIP3,
    sectionsById.get(API_FIELD_IDS.DESCRIPTION_IMEI_TOOLTIP4) ?? UI_TEXT_CONSTANTS.DEFAULTS.DESCRIPTION_IMEI_TOOLTIP4,
  ];
  const eligibleReasonCode = `${sectionsById.get(API_FIELD_IDS.ELIGIBLE_REASON_CODE) ?? ''}|${sectionsById.get(API_FIELD_IDS.ELIGIBLE_REASON_CODE_1) ?? ''}|${sectionsById.get(API_FIELD_IDS.ELIGIBLE_REASON_CODE_2) ?? ''}`;
  const pageTitle = sectionsById.get(API_FIELD_IDS.PAGE_TITLE) ?? UI_TEXT_CONSTANTS.DEFAULTS.PAGE_TITLE;
  const pageDescription = sectionsById.get(API_FIELD_IDS.PAGE_DESCRIPTION) ?? UI_TEXT_CONSTANTS.DEFAULTS.PAGE_DESCRIPTION;
  const customerSupportContacts = sectionsById.get(API_FIELD_IDS.CUSTOMER_SUPPORT_CONTACTS) ?? '';
  // Generate policy descriptions dynamically using helper function
  const modalContent: string[] = getUnlockingPolicyFieldIds().map(id => 
    sectionsById.get(id) ?? ''
  );

  // Generate FAQ items dynamically by detecting how many exist in the data
  const faqItems = [];
  let faqIndex = 1;
  let consecutiveMissing = 0;
  const MAX_CONSECUTIVE_MISSING = 3; // Stop after 3 consecutive missing FAQs
  
  while (consecutiveMissing < MAX_CONSECUTIVE_MISSING) {
    // Handle special case for 'faq_qone' as first question
    const qId = faqIndex === 1 ? 'faq_qone' : `faq_q${faqIndex}`;
    const aId = `faq_a${faqIndex}`;
    
    const title = sectionsById.get(qId) ?? (faqIndex === 1 ? sectionsById.get('faq_q1') : '') ?? '';
    const body = sectionsById.get(aId) ?? '';
    
    if (title && body) {
      faqItems.push({ id: `faq_${faqIndex}`, title, body });
      consecutiveMissing = 0; // Reset counter when we find an FAQ
    } else {
      consecutiveMissing++;
    }
    
    faqIndex++;
  }

  // Create the adapted structure
  return {
    responseInfo: (realApiResponse.responseInfo as ResponseInfo) ?? {
      timeStamp: new Date().toISOString(),
      responseCode: '200',
      responseMessage: 'Success',
      sectionErrors: []
    },
    body: {
      type: 'DEVICE_UNLOCK_STATUS',
      components: [
        // Main hero section AFTER header
        {
          type: 'HERO',
          display: 'DEFAULT',
          details: {
            title: titleText,
            description: '',
            body: '',
            content: {
              section: [
                {
                  id: 'title',
                  value: titleText
                },
                {
                  id: 'description',
                  value: titleDescription
                },
                {
                  id: 'link',
                  value: linkText,
                  cta: {
                    type: 'link',
                    text: linkText,
                    action: 'modal',
                    prefix: prefixLinkText,
                    modalTitle: titleUnlockingPolicy,
                    modalContent: modalContent
                  }
                }
              ]
            }
          }
        },
        // IMEI Form section
        {
          type: 'FORM',
          display: 'FORM',
          details: {
            title: titleText,
            body: titleText,
            content: {
              section: [
                {
                  id: 'imei_input_section',
                  title: '',
                  description: '',
                  input: {
                    id: 'imei_field',
                    title: inputText,
                    type: 'text',
                    required: true,
                    errorMessages: {
                      required: imeiFieldRequired,
                      invalid: imeiFieldInvalid
                    },
                    modalTitle: imeiToolTipTitle,
                    modalContent: imeiToolTipContent,
                    modalButtonText: imeiToolTipCTA
                  }
                },
                {
                  id: 'submit_button_section',
                  cta: {
                    type: 'button',
                    text: checkStatusCTA,
                    action: 'submit',
                    style: 'primary'
                  }
                }
              ]
            }
          }
        },
        // FAQ Section
        {
          type: 'FAQ',
          display: 'ACCORDION',
          details: {
            title: sections.find((s: ApiSection) => s.id === 'faq_title')?.value ?? 'Frequently Asked Questions',
            description: '',
            body: '',
            content: {
              section: faqItems
            }
          }
        }
      ],
      pageTitle: pageTitle,
      pageDescription: pageDescription,
      customerSupportContacts: customerSupportContacts,
      eligibleReasonCode: eligibleReasonCode,
    }
  };
};

/**
 * Adapt device status responses (locked, unlocked, eligible, ineligible)
 * Note: For eligible/ineligible scenarios, we navigate to separate pages with hardcoded forms
 * This adapter is used for locked/unlocked scenarios only
 */
export const adaptStatusResponse = (realApiResponse: RealApiResponse | DeviceUnlockResponse): DeviceUnlockResponse => {
  log.debug('adaptStatusResponse called');
  
  // If it's already in the correct format (mock), return as is
  if (realApiResponse?.body?.components) {
    log.debug('Response already in correct format (body.components)');
    return realApiResponse as DeviceUnlockResponse;
  }

  // For real API responses that may have a different structure, return as-is
  // The /eligible and /not-eligible pages use hardcoded forms, so we don't need to transform
  log.debug('Returning real API response as-is');
  return realApiResponse as DeviceUnlockResponse;
};
